import { motion, useMotionValue, useMotionTemplate } from 'framer-motion'
import { useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath, type StaticPageKey } from '../i18n/routes'

type Service = {
  pageKey: Extract<StaticPageKey, `service.${string}`>
  title: string
  desc: string
  bullets: string[]
  Icon: (props: { hover: boolean }) => JSX.Element
}

/* -------------------- Animated icons -------------------- */

const ICON_PROPS = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

function StockageIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg {...ICON_PROPS} initial="rest" animate={hover ? 'hover' : 'rest'}>
      <rect x="3" y="17" width="18" height="4" rx="1" />
      <motion.g
        variants={{ rest: { y: 8, opacity: 0 }, hover: { y: 0, opacity: 1 } }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <rect x="3" y="13" width="8" height="4" rx="1" />
      </motion.g>
      <motion.g
        variants={{ rest: { y: 8, opacity: 0 }, hover: { y: 0, opacity: 1 } }}
        transition={{ duration: 0.45, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
      >
        <rect x="13" y="13" width="8" height="4" rx="1" />
      </motion.g>
      <motion.g
        variants={{ rest: { y: 8, opacity: 0 }, hover: { y: 0, opacity: 1 } }}
        transition={{ duration: 0.45, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
      >
        <rect x="8" y="9" width="8" height="4" rx="1" />
      </motion.g>
    </motion.svg>
  )
}

function PickPackIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg {...ICON_PROPS} initial="rest" animate={hover ? 'hover' : 'rest'}>
      <rect x="4" y="9" width="16" height="12" rx="1.2" />
      <motion.path
        d="M4 14h16"
        variants={{ rest: { pathLength: 0, opacity: 0 }, hover: { pathLength: 1, opacity: 1 } }}
        transition={{ duration: 0.4 }}
      />
      <motion.path
        d="M12 9v12"
        variants={{ rest: { pathLength: 0, opacity: 0 }, hover: { pathLength: 1, opacity: 1 } }}
        transition={{ duration: 0.4, delay: 0.18 }}
      />
      <motion.path
        d="M9 5.5C9 4 11 3.5 12 5.5C13 3.5 15 4 15 5.5C15 7 12 9 12 9C12 9 9 7 9 5.5Z"
        variants={{
          rest: { scale: 0, opacity: 0 },
          hover: { scale: 1, opacity: 1 },
        }}
        style={{ transformOrigin: '12px 7px' }}
        transition={{ duration: 0.35, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      />
    </motion.svg>
  )
}

function ShippingIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg {...ICON_PROPS} initial="rest" animate={hover ? 'hover' : 'rest'}>
      {/* speed lines */}
      <motion.path
        d="M2 9h2"
        variants={{ rest: { opacity: 0, x: 6 }, hover: { opacity: 1, x: 0 } }}
        transition={{ duration: 0.35, delay: 0.05 }}
      />
      <motion.path
        d="M1 12h3"
        variants={{ rest: { opacity: 0, x: 6 }, hover: { opacity: 1, x: 0 } }}
        transition={{ duration: 0.35, delay: 0.12 }}
      />
      <motion.path
        d="M2 15h2"
        variants={{ rest: { opacity: 0, x: 6 }, hover: { opacity: 1, x: 0 } }}
        transition={{ duration: 0.35, delay: 0.18 }}
      />
      {/* truck */}
      <motion.g
        variants={{ rest: { x: -1.5 }, hover: { x: 1.2 } }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <path d="M5 7h9v10H5z" />
        <path d="M14 11h4l3 3v3h-7z" />
        <circle cx="8.5" cy="18" r="1.5" />
        <circle cx="17.5" cy="18" r="1.5" />
      </motion.g>
    </motion.svg>
  )
}

function ReturnsIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg {...ICON_PROPS} initial="rest" animate={hover ? 'hover' : 'rest'}>
      <motion.g
        variants={{ rest: { rotate: 0 }, hover: { rotate: -360 } }}
        style={{ transformOrigin: '12px 12px' }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <path d="M20 12a8 8 0 1 1-2.5-5.8" />
        <path d="M20 4v5h-5" />
      </motion.g>
    </motion.svg>
  )
}

/* -------------------- Card -------------------- */

function ServiceCard({ s, index }: { s: Service; index: number }) {
  const { t } = useT()
  const locale = useLocale()
  const [hover, setHover] = useState(false)
  const mouseX = useMotionValue(-500)
  const mouseY = useMotionValue(-500)

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    mouseX.set(e.clientX - r.left)
    mouseY.set(e.clientY - r.top)
  }
  const onLeave = () => {
    setHover(false)
    mouseX.set(-500)
    mouseY.set(-500)
  }

  const spotlight = useMotionTemplate`radial-gradient(340px circle at ${mouseX}px ${mouseY}px, rgba(59,93,255,0.10), transparent 65%)`

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.97, filter: 'blur(6px)' }}
      whileInView={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="h-full"
    >
      {/* Double-Bezel : plateau gris (shell) autour d'une plaque blanche (core),
          rayons concentriques — la carte lit comme un objet usiné, plus comme
          un aplat gris. Le plateau garde la teinte de l'ancien fond de carte. */}
      <motion.div
        onMouseEnter={() => setHover(true)}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        animate={{ y: hover ? -4 : 0 }}
        transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
        className="group relative h-full rounded-[1.5rem] bg-ink-950/[0.04] p-1.5 ring-1 ring-ink-950/[0.06] transition-shadow duration-500 hover:shadow-[0_34px_80px_-34px_rgba(36,47,214,0.35)]"
      >
        <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(1.5rem-0.375rem)] border border-ink-950/[0.05] bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
          {/* cursor spotlight */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -inset-px rounded-[calc(1.5rem-0.375rem)] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: spotlight }}
          />

          {/* corner halo */}
          <motion.div
            aria-hidden
            animate={{ opacity: hover ? 0.7 : 0.25, scale: hover ? 1.12 : 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-none absolute -top-14 -right-14 w-44 h-44 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(59,93,255,0.16), rgba(59,93,255,0) 70%)',
              filter: 'blur(22px)',
            }}
          />

          {/* Icon */}
          <motion.div
            animate={{ y: hover ? -2 : 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-accent-600 to-accent-700 text-white flex items-center justify-center shadow-[0_14px_36px_-12px_rgba(59,93,255,0.7)]"
          >
            {/* glossy ring */}
            <span className="absolute inset-0 rounded-xl ring-1 ring-white/20" />
            {/* shimmer sweep on hover */}
            <motion.span
              aria-hidden
              animate={{ x: hover ? '120%' : '-120%' }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
              className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none"
            >
              <span className="block w-1/2 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            </motion.span>
            <s.Icon hover={hover} />
          </motion.div>

          <h3 className="mt-5 text-xl font-semibold text-ink-950 tracking-tight">{s.title}</h3>
          <p className="mt-2 text-[15px] text-ink-600 leading-relaxed">{s.desc}</p>

          {/* bullets — visibles en permanence (l'ancien hover les cachait sur
              mobile et pour le crawler), séparées par une hairline */}
          <ul className="mt-5 border-t border-ink-950/[0.06] pt-4 space-y-2">
            {s.bullets.map((b, i) => (
              <motion.li
                key={b}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: 0.25 + index * 0.1 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-2.5 text-sm text-ink-700"
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b5dff" strokeWidth="2.6">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {b}
              </motion.li>
            ))}
          </ul>

          {/* CTA bouton-dans-bouton, ancré en bas pour que les 4 cartes alignent
              leur lien sur une même ligne quelles que soient les longueurs */}
          <Link
            to={localizedPath(s.pageKey, locale)}
            className="mt-auto pt-6 inline-flex items-center gap-2.5 text-sm font-medium text-accent-700 hover:text-accent-800 transition-colors self-start"
          >
            <span className="relative">
              {t('services.learnMore')}
              <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-accent-700 group-hover:w-full transition-[width] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]" />
            </span>
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-50 ring-1 ring-accent-100 transition-colors duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-accent-700 group-hover:ring-accent-700">
              <motion.svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                animate={{ x: hover ? 1.5 : 0, y: hover ? -1.5 : 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="text-accent-700 transition-colors duration-500 group-hover:text-white"
              >
                <path d="M7 17L17 7M17 7H8M17 7v9" strokeLinecap="round" strokeLinejoin="round" />
              </motion.svg>
            </span>
          </Link>
        </div>
      </motion.div>
    </motion.div>
  )
}

/* -------------------- Section -------------------- */

export default function Services() {
  const { t } = useT()

  const services: Service[] = [
    {
      pageKey: 'service.storage',
      title: t('services.storageTitle'),
      desc: t('services.storageDesc'),
      bullets: [
        t('services.storageBullet1'),
        t('services.storageBullet2'),
        t('services.storageBullet3'),
      ],
      Icon: StockageIcon,
    },
    {
      pageKey: 'service.pickpack',
      title: t('services.pickpackTitle'),
      desc: t('services.pickpackDesc'),
      bullets: [
        t('services.pickpackBullet1'),
        t('services.pickpackBullet2'),
        t('services.pickpackBullet3'),
      ],
      Icon: PickPackIcon,
    },
    {
      pageKey: 'service.shipping',
      title: t('services.shippingTitle'),
      desc: t('services.shippingDesc'),
      bullets: [
        t('services.shippingBullet1'),
        t('services.shippingBullet2'),
        t('services.shippingBullet3'),
      ],
      Icon: ShippingIcon,
    },
    {
      pageKey: 'service.returns',
      title: t('services.returnsTitle'),
      desc: t('services.returnsDesc'),
      bullets: [
        t('services.returnsBullet1'),
        t('services.returnsBullet2'),
        t('services.returnsBullet3'),
      ],
      Icon: ReturnsIcon,
    },
  ]

  return (
    <section id="services" className="relative isolate py-28 overflow-hidden">
      {/* Background layer (DA aligned with Hero/Carriers) */}
      <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
        {/* top fade from previous section */}
        <div
          className="absolute inset-x-0 -top-20 h-40"
          style={{
            background: 'linear-gradient(to bottom, rgba(59,93,255,0.04), transparent)',
          }}
        />
        {/* dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage:
              'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          }}
        />
        {/* breathing halo */}
        <motion.div
          animate={{ opacity: [0.3, 0.55, 0.3] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute right-[12%] top-[18%] w-[480px] h-[480px] rounded-full"
          style={{
            background:
              'radial-gradient(ellipse, rgba(59,93,255,0.16), rgba(59,93,255,0.04) 45%, transparent 75%)',
            filter: 'blur(60px)',
          }}
        />
        <motion.div
          animate={{ opacity: [0.25, 0.5, 0.25] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
          className="absolute left-[8%] bottom-[12%] w-[420px] h-[420px] rounded-full"
          style={{
            background:
              'radial-gradient(ellipse, rgba(59,93,255,0.13), rgba(59,93,255,0.03) 45%, transparent 75%)',
            filter: 'blur(70px)',
          }}
        />
        {/* bottom fade */}
        <div
          className="absolute inset-x-0 -bottom-1 h-32"
          style={{
            background: 'linear-gradient(to bottom, transparent, rgba(14,14,20,0.03))',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{t('services.badgeLabel')}</span>
            <span className="text-accent-400">·</span>
            <span className="text-accent-700">{t('services.badgeTagline')}</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-semibold tracking-[-0.02em] text-ink-950 text-[clamp(2rem,4.5vw,3.25rem)] leading-[1.05]"
          >
            {t('services.headlinePart1')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('services.headlinePart2')}
            </span>
            .
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.18 }}
            className="mt-5 text-ink-600 text-base sm:text-lg leading-relaxed"
          >
            {t('services.subhead')}
          </motion.p>
        </div>

        {/* Grid */}
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <ServiceCard key={s.pageKey} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
